import { type ReactNode } from 'react';
import { ChevronRight, Folder, Lock } from 'lucide-react';
import type { PropertyFileTreeNode } from '@/shared/api';
import { formatBytes } from '@/shared/lib/format';
import { topFolderDescription } from './akteFolderMeta';
import { folderDisplayName } from './akteFolderNames';
import { isCustomFolder } from './akteFolderRules';

interface Props {
  node: PropertyFileTreeNode;
  locked?: boolean;
  onNavigate: (path: string) => void;
  /** Kontextmenü (Custom-Ordner) — von C2 injiziert. */
  menu?: ReactNode;
  /** Drop-Target-Handler (Move per Drag) — von C2 injiziert. */
  dropProps?: React.HTMLAttributes<HTMLLIElement>;
  dropActive?: boolean;
}

/** Ordner-Zeile in der Content-Liste: navigiert hinein; „Eigener Ordner" bei Custom-Folders. */
export function AkteContentFolderRow({
  node,
  locked,
  onNavigate,
  menu,
  dropProps,
  dropActive,
}: Props) {
  const name = folderDisplayName(node.path);
  const count = node.descendantFileCount;
  // Bei einer Top-Kategorie erklärt die Beschreibung, was hier hingehört — genau die
  // Information, die die Ordner-Rail nicht zeigt. Sonst bleibt es bei der Herkunft.
  const subline =
    topFolderDescription(node.path) ?? (isCustomFolder(node.path) ? 'Eigener Ordner' : null);
  const volume =
    count === 0
      ? 'noch leer'
      : `${count} ${count === 1 ? 'Datei' : 'Dateien'} · ${formatBytes(node.totalSizeBytes)}`;

  return (
    <li
      {...dropProps}
      className={
        'group flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors sm:gap-3.5 sm:px-3.5 sm:py-3 ' +
        (dropActive
          ? 'border-sealwax bg-sealwax/15 ring-sealwax/40 ring-2'
          : 'hover:bg-card-alt/50 border-transparent')
      }
    >
      <button
        type="button"
        onClick={() => onNavigate(node.path)}
        // Der Speicher-Schlüssel bleibt als Zweitinformation greifbar, ohne die Zeile zu führen.
        title={node.path}
        className="flex min-h-11 min-w-0 flex-1 items-center gap-3 text-left sm:gap-3.5"
      >
        <span className="bg-card text-ink-soft flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
          <Folder size={19} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-ink truncate text-[15px] font-medium">{name}</span>
            {locked && <Lock size={13} className="text-ink-faint shrink-0" aria-label="Gesperrt" />}
          </span>
          {subline && (
            <span className="text-ink-faint mt-0.5 block truncate text-[13px]">{subline}</span>
          )}
          {/* Der Umfang steht mobil unter dem Namen und ab sm rechts in eigener Spalte. */}
          <span className="text-ink-faint mt-0.5 block truncate text-[13px] tabular-nums sm:hidden">
            {volume}
          </span>
        </span>
      </button>
      <span className="text-ink-faint hidden shrink-0 text-[13px] tabular-nums sm:inline">
        {volume}
      </span>
      <ChevronRight size={16} className="text-navy shrink-0" aria-hidden />
      {menu}
    </li>
  );
}
